import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../../libs/environment/environment';

export interface CrmAuthError {
  code: string;
  message: string;
}

export interface CrmApiResponse<TData> {
  isSuccess: boolean;
  success?: boolean;
  data: TData;
  message: string;
  error?: CrmAuthError;
  timestamp: string;
}

export interface CrmLoginRequest {
  email: string;
  password: string;
}

export interface CrmLoginResponseData {
  challengeToken: string;
  otpExpiresInSeconds: number;
  maxAttempts: number;
  resendAvailableInSeconds: number;
  maxResends: number;
}

export interface CrmResend2faRequest {
  challengeToken: string;
}

export interface CrmResend2faResponseData {
  challengeToken: string;
  otpExpiresInSeconds: number;
  resendAvailableInSeconds: number;
  remainingResends: number;
}

export interface CrmVerify2faRequest {
  challengeToken: string;
  otp: string;
}

export interface CrmPermissionActionPayload {
  supported?: boolean;
  enabled?: boolean;
}

export interface CrmPermissionNodePayload {
  is_parent?: boolean;
  permissions?: Record<string, CrmPermissionActionPayload> | string[];
}

export interface CrmPermissionModulesPayload {
  [moduleKey: string]: Record<string, CrmPermissionNodePayload>;
}

export interface CrmPermissionsPayload {
  permissions?: CrmPermissionModulesPayload;
}

export interface CrmVerifyUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userApplicationStatus: string;
  userOnboardingStep: string;
  tenantId: number;
  branchId: number;
  arId: number | null;
  departmentId: number;
  designationId: number;
  departmentName: string;
  designationName: string;
  branchName: string;
  arName: string | null;
  profilePhotoUrl?: string;
  profileImageUrl?: string;
  profileImage?: string;
}

export interface CrmVerify2faResponseData {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
  accessTokenExpiresUtc: string;
  refreshTokenExpiresUtc: string;
  resendAvailableInSeconds: number;
  applicationStatus: string;
  creationFlow: string;
  tenantCompanyName?: string;
  permissions: CrmPermissionsPayload;
  user: CrmVerifyUser;
}

export interface CrmRefreshTokenRequest {
  refreshToken: string;
}

export interface CrmRefreshTokenResponseData {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
  accessTokenExpiresUtc: string;
  refreshTokenExpiresUtc: string;
}

export interface CrmOnboardingStatusResponseData {
  applicationStatus: string;
  onboardingStatus: string;
  creationFlow: string;
}

export interface CrmLogoutRequest {
  refreshToken: string;
}

export interface CrmLogoutResponseData {
  message?: string;
}

export interface CrmAgreementAcceptResponseData {
  message?: string;
}

export interface CrmFinalHqBranchData {
  companyName: string;
  mobileNumber: string;
  companyEmail: string;
  complianceEmail: string;
  postCode: string;
  companyAddress: string;
  city: string;
  country: string;
  userCredits?: number;
}

export interface CrmMeResponseData {
  id?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  tenantId?: number;
  tenantCompanyName?: string;
  branchId?: number;
  branchName?: string;
  arId?: number | null;
  arName?: string | null;
  departmentId?: number;
  departmentName?: string;
  designationId?: number;
  designationName?: string;
  profilePhotoUrl?: string;
  profileImageUrl?: string;
  profileImage?: string;
  permissions?: CrmPermissionsPayload;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class CrmAuthApiService {
  private readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  login(payload: CrmLoginRequest): Observable<CrmApiResponse<CrmLoginResponseData>> {
    return this.http.post<CrmApiResponse<CrmLoginResponseData>>(
      `${this.apiBaseUrl}/v1/business/auth/login`,
      payload,
    );
  }

  resend2fa(payload: CrmResend2faRequest): Observable<CrmApiResponse<CrmResend2faResponseData>> {
    return this.http.post<CrmApiResponse<CrmResend2faResponseData>>(
      `${this.apiBaseUrl}/v1/business/auth/resend-2fa`,
      payload,
    );
  }

  verify2fa(payload: CrmVerify2faRequest): Observable<CrmApiResponse<CrmVerify2faResponseData>> {
    return this.http.post<CrmApiResponse<CrmVerify2faResponseData>>(
      `${this.apiBaseUrl}/v1/business/auth/verify-2fa`,
      payload,
    );
  }

  getOnboardingStatus(accessToken: string): Observable<CrmApiResponse<CrmOnboardingStatusResponseData>> {
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
      : undefined;

    return this.http.get<CrmApiResponse<CrmOnboardingStatusResponseData>>(
      `${this.apiBaseUrl}/v1/business/tenant/onboarding/status`,
      { headers },
    );
  }

  refreshToken(payload: CrmRefreshTokenRequest): Observable<CrmApiResponse<CrmRefreshTokenResponseData>> {
    return this.http.post<CrmApiResponse<CrmRefreshTokenResponseData>>(
      `${this.apiBaseUrl}/v1/business/auth/refresh`,
      payload,
    );
  }

  logout(payload: CrmLogoutRequest): Observable<CrmApiResponse<CrmLogoutResponseData>> {
    return this.http.post<CrmApiResponse<CrmLogoutResponseData>>(
      `${this.apiBaseUrl}/v1/business/auth/logout`,
      payload,
    );
  }

  acceptAgreement(accessToken: string): Observable<CrmApiResponse<CrmAgreementAcceptResponseData>> {
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
      : undefined;

    return this.http.post<CrmApiResponse<CrmAgreementAcceptResponseData>>(
      `${this.apiBaseUrl}/v1/business/tenant/onboarding/agreement/accept`,
      {},
      { headers },
    );
  }

  getFinalHqBranch(accessToken: string): Observable<CrmApiResponse<CrmFinalHqBranchData>> {
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
      : undefined;

    return this.http.get<CrmApiResponse<CrmFinalHqBranchData>>(
      `${this.apiBaseUrl}/v1/business/tenant/onboarding/final/hq-branch`,
      { headers },
    );
  }

  saveFinalHqBranch(accessToken: string): Observable<CrmApiResponse<null>> {
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
      : undefined;

    return this.http.post<CrmApiResponse<null>>(
      `${this.apiBaseUrl}/v1/business/tenant/onboarding/final/hq-branch`,
      {},
      { headers },
    );
  }

  getBusinessMe(accessToken: string): Observable<CrmApiResponse<CrmMeResponseData>> {
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
      : undefined;

    return this.http.get<CrmApiResponse<CrmMeResponseData>>(`${this.apiBaseUrl}/v1/business/me`, {
      headers,
    });
  }
}
